// The eight sided die, drawn as flat SVG triangles. Each face that points at
// the viewer fills with a dither, and how much light the face gets picks the
// tone. The dither is a pattern in screen space, so its cells stay on the pixel
// grid while the die turns under them. All vector.

import {
  TUMBLE_AMBIENT_CELLS,
  TUMBLE_COIN_RADIUS,
  TUMBLE_COIN_SPREAD,
  TUMBLE_LIGHT,
  TUMBLE_NUMBER_SIZE,
  TUMBLE_SIDE_MARK_BAND,
  TUMBLE_SIDE_MARK_SIZE,
} from '../constants';
import { DIE_FACES, type DieFace, type DieFaceLabel } from '../dice/octahedron';
import type { TumblePose } from '../dice/tumble';
import { dot, normalize, rotate, type Vec3 } from '../dice/vector';
import type { Side } from '../engine/types';
import { setAttributes, svg } from '../lib/svg';
import { createTonePattern, DITHER_TONE_COUNT } from './ditherTone';
import { coinStarPath } from './energyStar';
import { MARK_BOX, SIDE_MARK_SHAPES } from './SideMark';

const LIGHT = normalize(TUMBLE_LIGHT);

/** A face that looks at the light as squarely as it looks at the viewer is fully lit. */
const toneOf = (normal: Vec3): number => {
  const lit = Math.min(Math.max(dot(normal, LIGHT) / LIGHT[2], 0), 1);
  return Math.round(TUMBLE_AMBIENT_CELLS + (DITHER_TONE_COUNT - TUMBLE_AMBIENT_CELLS) * lit);
};

type FaceParts = {
  face: DieFace;
  group: SVGGElement;
  plate: SVGPolygonElement;
  number: SVGTextElement;
  /** The coins of an energy face, in place of the number. One unit is the size of the number. */
  coins: SVGGElement;
};

const faceParts = (face: DieFace, index: number): FaceParts => {
  const plate = svg('polygon', { class: 'die-solid__plate' });
  const number = svg('text', { class: 'die-solid__number' }, [String(index + 1)]);
  const coins = svg('g', { class: 'die-solid__coins' });
  return {
    face,
    plate,
    number,
    coins,
    group: svg('g', { class: 'die-solid__face' }, [plate, number, coins]),
  };
};

/** `count` coins in a row, about the middle of the face: for each, a disc and the star of energy on it. */
const coinDiscs = (count: number): SVGElement[] =>
  Array.from({ length: count }, (_, place) => {
    const cx = Number(((place - (count - 1) / 2) * TUMBLE_COIN_SPREAD * 2).toFixed(3));
    return [
      svg('circle', { cx, cy: 0, r: TUMBLE_COIN_RADIUS }),
      svg('path', { class: 'die-solid__star', d: coinStarPath(TUMBLE_COIN_RADIUS, cx) }),
    ];
  }).flat();

/**
 * The mark of a side, about the middle of the face, for the coin flip of a room.
 * The mark is drawn in a box of `MARK_BOX`, so its group scales it to the size of
 * the number. Its band of the light color keeps the width the coins have.
 */
const sideMark = (side: Side): SVGGElement => {
  const fit = TUMBLE_SIDE_MARK_SIZE / MARK_BOX;
  return svg(
    'g',
    {
      transform: `scale(${fit}) translate(${-MARK_BOX / 2} ${-MARK_BOX / 2})`,
      'stroke-width': (TUMBLE_SIDE_MARK_BAND / fit).toFixed(3),
    },
    SIDE_MARK_SHAPES[side](),
  );
};

export type DieSolid = {
  /** A group to put in an SVG whose units are screen pixels. */
  group: SVGGElement;
  defs: SVGDefsElement;
  draw: (pose: TumblePose, radius: number) => void;
  /** What each face reads, by face. The die starts with the numbers one to eight. */
  label: (labels: readonly DieFaceLabel[]) => void;
};

/** `toneIdPrefix` names the patterns of this die. Two dice on one page need two prefixes. */
export const createDieSolid = (toneIdPrefix: string): DieSolid => {
  const faces = DIE_FACES.map(faceParts);
  const group = svg(
    'g',
    { class: 'die-solid' },
    faces.map((parts) => parts.group),
  );
  const defs = svg(
    'defs',
    {},
    Array.from({ length: DITHER_TONE_COUNT + 1 }, (_, litCells) =>
      createTonePattern(
        `${toneIdPrefix}${litCells}`,
        litCells,
        'die-solid__light',
        'die-solid__shade',
      ),
    ),
  );

  const draw = (pose: TumblePose, radius: number): void => {
    const size = radius * pose.scale;
    const onScreen = (point: Vec3): string =>
      `${(pose.x + point[0] * size).toFixed(2)},${(pose.y - point[1] * size).toFixed(2)}`;
    // A landing animation scales the die about its own center.
    group.style.transformOrigin = `${pose.x}px ${pose.y}px`;

    // The face that looks most squarely at the viewer has no dither: it is fully lit, whatever
    // the light gives it. It holds the number or the coins the player reads. Garrett,
    // 2026-10-12: "dont do those squares on the front face of the die ... front face always
    // stays plain from dither".
    const normals = faces.map(({ face }) => rotate(pose.orientation, face.normal));
    const front = normals.reduce((best, normal, index) => {
      const bestNormal = normals[best];
      return bestNormal && normal[2] > bestNormal[2] ? index : best;
    }, 0);

    for (const [index, { face, group: faceGroup, plate, number, coins }] of faces.entries()) {
      const normal = normals[index];
      if (!normal) continue;
      const showing = normal[2] > 0;
      faceGroup.setAttribute('display', showing ? 'inline' : 'none');
      if (!showing) continue;

      setAttributes(plate, {
        points: face.corners.map((corner) => onScreen(rotate(pose.orientation, corner))).join(' '),
        fill: `url(#${toneIdPrefix}${index === front ? DITHER_TONE_COUNT : toneOf(normal)})`,
      });

      // The number lies in the plane of the face, so it leans as the face does. It keeps one
      // color on every tone of the face. A number that changed color with the tone flickered.
      const right = rotate(pose.orientation, face.right);
      const up = rotate(pose.orientation, face.up);
      const center = rotate(pose.orientation, face.center);
      const unit = size * TUMBLE_NUMBER_SIZE;
      const middle = `${pose.x + center[0] * size} ${pose.y - center[1] * size}`;
      setAttributes(number, {
        'font-size': unit.toFixed(2),
        transform: `matrix(${right[0]} ${-right[1]} ${-up[0]} ${up[1]} ${middle})`,
      });
      // The coins have no font size, so the matrix has the size of the number in it.
      setAttributes(coins, {
        transform: `matrix(${right[0] * unit} ${-right[1] * unit} ${-up[0] * unit} ${up[1] * unit} ${middle})`,
      });
    }
  };

  const label = (labels: readonly DieFaceLabel[]): void => {
    faces.forEach((parts, index) => {
      const label = labels[index] ?? String(index + 1);
      const isText = typeof label === 'string';
      parts.number.textContent = isText ? label : '';
      const marks = isText
        ? []
        : 'coins' in label
          ? coinDiscs(label.coins)
          : [sideMark(label.side)];
      parts.coins.replaceChildren(...marks);
    });
  };

  return { group, defs, draw, label };
};
